import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import {
  Search,
  TableProperties,
  ChevronLeft,
  ChevronRight,
  Plus,
  Eye,
  Edit2,
  Trash2,
  Package
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AddOneButton from "../../components/common/AddOneButton";
import ProductListSkeleton from "../../components/AdminSideComponents/DashBoardProduct/ProductListSkeleton";
import StockChangeModal from "../../components/AdminSideComponents/DashBoardProduct/StockChangeModal";
import DeleteAlertModal from "../../components/common/DeleteAlertModal";
import { useAuth } from "../../context/AuthContext";
import Swal from "sweetalert2";
import toast from "react-hot-toast";

// Helper: extract a readable string from { en, hi, es, zh }
const getLocalizedString = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    // Prefer English, then Hindi, Spanish, Chinese, or any available
    return value.en || value.hi || value.es || value.zh || Object.values(value)[0] || "";
  }
  return "";
};

const SupplierProducts = () => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [stockStatus, setStockStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(8);
  const [error, setError] = useState(null);
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [cHngModalData, setCHngModalData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const navigate = useNavigate();
  const { user } = useAuth();

  const getSupplierId = () => {
    return user?.supplierId || user?.supplier?._id || user?._id || null;
  };

  const fetchProducts = useCallback(async () => {
    const supplierId = getSupplierId();

    if (!supplierId) {
      setError("Supplier ID not found.");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/products/products-by-supplier/${supplierId}`
      );

      const apiProducts = response.data?.products ?? [];

      // Normalize products: convert multilingual name fields to English strings
      const normalizedProducts = apiProducts
        .filter((p) => {
          const prodSupplierId =
            typeof p.supplier === "string" ? p.supplier : p.supplier?._id;
          return prodSupplierId === supplierId;
        })
        .map((p) => ({
          ...p,
          name: getLocalizedString(p.name),
          category: p.category
            ? { ...p.category, name: getLocalizedString(p.category.name) }
            : p.category,
          subCategory: p.subCategory
            ? { ...p.subCategory, name: getLocalizedString(p.subCategory.name) }
            : p.subCategory,
          material: getLocalizedString(p.material),
          finishType: getLocalizedString(p.finishType),
          origin: getLocalizedString(p.origin),
          careInstruction: getLocalizedString(p.careInstruction),
          return_policy: getLocalizedString(p.return_policy),
          measurement: getLocalizedString(p.measurement),
          // If features is an array of objects, you can also map them if needed
          // But for search/filter we only use name, category, subCategory
        }));

      setProducts(normalizedProducts);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Failed to load products.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // SEARCH + FILTER (now all names are strings)
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const searchMatch =
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.category?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.subCategory?.name?.toLowerCase().includes(searchTerm.toLowerCase());

      const stockMatch = !stockStatus || p.stock_status === stockStatus;

      return searchMatch && stockMatch;
    });
  }, [products, searchTerm, stockStatus]);

  // PAGINATION
  const totalPages = Math.ceil(filteredProducts.length / entriesPerPage);
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesPerPage;
    return filteredProducts.slice(startIndex, startIndex + entriesPerPage);
  }, [filteredProducts, currentPage, entriesPerPage]);

  const getStockBadgeClass = (stock) => {
    switch (stock) {
      case "active":
        return "bg-green-100 text-green-600";
      case "inactive":
        return "bg-gray-100 text-gray-600";
      case "out of stock":
        return "bg-red-100 text-red-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  // STOCK MODAL
  const handleOpenStockModal = (product) => {
    setCHngModalData({
      prod_id: product._id,
      prod_status: product.stock_status,
      prod_name: product.name, // now a string
    });
    setIsStockModalOpen(true);
  };

  // DELETE PRODUCT
  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    setDeleteLoading(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/products/delete-product/${productToDelete._id}`
      );

      setShowDeleteModal(false);
      Swal.fire("Deleted!", `${productToDelete.name} removed`, "success");
      fetchProducts();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete product");
    } finally {
      setDeleteLoading(false);
      setProductToDelete(null);
    }
  };

  return (
    <section className="min-h-screen p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-xl md:text-3xl font-philosopher text-primaryTextColor flex items-center gap-2">
          <TableProperties className="text-primaryColor w-7 h-7" />
          My Products
        </h1>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
            <select
              value={stockStatus}
              onChange={(e) => setStockStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 w-full md:w-56"
            >
              <option value="">All Stocks</option>
              <option value="active">In Stock</option>
              <option value="inactive">Inactive</option>
              <option value="out of stock">Out of Stock</option>
            </select>

            <div className="relative w-full md:w-96">
              <input
                type="text"
                placeholder="Search my products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded-lg pl-4 pr-10 py-2 w-full"
              />
              <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
            </div>
          </div>

          <AddOneButton
            onClick={() => navigate("/seller/add-product")}
            label="Add Product"
          />
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <ProductListSkeleton />
        ) : filteredProducts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-4 text-left font-medium">#</th>
                  <th className="px-4 py-4 text-left font-medium">Image</th>
                  <th className="px-4 py-4 text-left font-medium">Name</th>
                  <th className="px-4 py-4 text-left font-medium">Category</th>
                  <th className="px-4 py-4 text-left font-medium">Subcategory</th>
                  <th className="px-4 py-4 text-left font-medium">Price</th>
                  <th className="px-4 py-4 text-left font-medium">Stock</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProducts.map((product, index) => (
                  <tr key={product._id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-2">
                      {(currentPage - 1) * entriesPerPage + index + 1}
                    </td>
                    <td
                      onClick={() => navigate(`/seller/product-details/${product._id}`)}
                      className="px-4 py-2 cursor-pointer"
                    >
                      <img
                        src={product.images?.[0] || product.imgUrl}
                        alt={product.name}
                        className="w-12 h-12 object-cover rounded-lg border"
                      />
                    </td>
                    <td
                      onClick={() => navigate(`/seller/product-details/${product._id}`)}
                      className="px-4 py-2 cursor-pointer font-medium"
                    >
                      <span className="block sm:hidden">
                        {product.name.length > 6 ? product.name.slice(0, 6) + "..." : product.name}
                      </span>
                      <span className="hidden sm:block">{product.name}</span>
                    </td>
                    <td className="px-4 py-2">{product.category?.name}</td>
                    <td className="px-4 py-2">{product.subCategory?.name}</td>
                    <td className="px-4 py-2 font-medium">₹{product.price}</td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => handleOpenStockModal(product)}
                        className={`inline-block px-3 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 ${getStockBadgeClass(
                          product.stock_status
                        )}`}
                      >
                        {product.stock_status}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-10 text-gray-500">
            <Package className="w-16 h-16 mx-auto text-gray-300" />
            <p className="mt-4">No products found</p>
          </div>
        )}
      </div>

      <StockChangeModal
        isOpen={isStockModalOpen}
        onClose={() => setIsStockModalOpen(false)}
        cHngModalData={cHngModalData}
        getAllProducts={fetchProducts}
      />

      <DeleteAlertModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setProductToDelete(null);
        }}
        dltType="product"
        dltName={productToDelete?.name}
        onConfirm={handleDeleteConfirm}
        dltLoading={deleteLoading}
      />
    </section>
  );
};

export default SupplierProducts;